import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { VegetationSpeciesDef } from '@/planet/VegetationDef';
import {
  createShadowCascadeUniforms,
  GLSL_SHADOW_CASCADE,
  GLSL_SHADOW_CASCADE_UNIFORMS,
  ShadowCascadeUniforms,
} from '../shaders/shadowCascade';

/**
 * Procedural, low-poly vegetation shapes and materials.
 *
 * Nothing here is authored art: every species' mesh is a handful of merged
 * primitives built once at load time and reused (shared geometry/material)
 * across every instance and every chunk — only the per-instance transform and
 * tint differ. Colour comes from two multiplied sources rather than a
 * texture: a baked per-vertex tone (bark/stem vs. foliage/bloom) and a
 * per-instance tint interpolated between the species' `color` and
 * `detailColor` (see `VegetationLayer`'s use of `colorJitter`), matching the
 * vertex-colour philosophy the terrain mesh already uses instead of texturing
 * the ground.
 *
 * Two solid detail tiers ('high', 'medium') are built per species at its
 * average metric size from `heightRange`/`radiusRange`; a single shared unit
 * quad plus a small generated silhouette texture serves the billboard tier,
 * scaled per instance in `VegetationLayer`. Billboard textures are cached by
 * silhouette family, not by instance or chunk.
 */

export type SolidDetail = 'high' | 'medium';

// MeshStandardMaterial multiplies per-vertex and per-instance colours.  Bark
// therefore needs a warm compensating tone (>1 in red is intentional) or a
// green foliage tint turns trunks almost black.
const BARK_TONE: [number, number, number] = [1.5, 0.72, 0.36];
const STEM_TONE: [number, number, number] = [0.45, 0.55, 0.35];
const WHITE: [number, number, number] = [1, 1, 1];

/** Cheap, stable-per-call jitter — not part of the seeded world-generation path, just shape variety baked once at load. */
function jitter(i: number): number {
  const s = Math.sin(i * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

function paintVertexColor(geometry: THREE.BufferGeometry, color: readonly [number, number, number]): THREE.BufferGeometry {
  const count = geometry.attributes.position.count;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    colors[i * 3] = color[0];
    colors[i * 3 + 1] = color[1];
    colors[i * 3 + 2] = color[2];
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geometry;
}

/**
 * `mergeGeometries` refuses to mix indexed and non-indexed geometry in one
 * call (it logs an error and returns null rather than throwing, which is
 * worse: the failure only shows up later as an invisible or crashing mesh).
 * `THREE.IcosahedronGeometry` is built non-indexed; `THREE.CylinderGeometry`
 * is indexed. Every part fed to `mergeGeometries` below is normalised
 * through this first so the two never collide.
 */
function nonIndexed(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  return geometry.index ? geometry.toNonIndexed() : geometry;
}

/** A single tapering triangular blade: wide at the base, a point at the top. Non-indexed to match the foliage blobs it gets merged with. */
function buildBlade(height: number, width: number, bend = 0): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  const shoulderY = height * 0.62;
  const positions = new Float32Array([
    -width, 0, 0, width, 0, 0, width * 0.52 + bend * 0.35, shoulderY, bend * 0.35,
    -width, 0, 0, width * 0.52 + bend * 0.35, shoulderY, bend * 0.35, bend, height, bend * 0.6,
  ]);
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute(
    'uv',
    new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 0.78, 0.62, 0, 0, 0.78, 0.62, 0.5, 1]), 2),
  );
  geometry.computeVertexNormals();
  return geometry;
}

export type TreeSilhouette = 'broadleaf' | 'conifer' | 'umbrella' | 'palm' | 'fungal' | 'alien';

export function treeSilhouette(species: VegetationSpeciesDef): TreeSilhouette {
  const id = species.id.toLowerCase();
  if (id.includes('conifer') || id.includes('cypress')) return 'conifer';
  if (id.includes('acacia') || id.includes('lone')) return 'umbrella';
  if (id.includes('palm')) return 'palm';
  if (id.includes('fungal')) return 'fungal';
  if (id.includes('alien')) return 'alien';
  return 'broadleaf';
}

function addTrunk(
  parts: THREE.BufferGeometry[],
  height: number,
  radius: number,
  detail: SolidDetail,
  widthScale = 1,
): void {
  const trunk = nonIndexed(
    new THREE.CylinderGeometry(
      radius * 0.105 * widthScale,
      radius * 0.18 * widthScale,
      height,
      detail === 'high' ? 7 : 5,
    ),
  );
  trunk.translate(0, height / 2, 0);
  paintVertexColor(trunk, BARK_TONE);
  parts.push(trunk);
}

function buildBroadleafTree(height: number, radius: number, detail: SolidDetail, umbrella: boolean): THREE.BufferGeometry {
  const trunkHeight = height * (umbrella ? 0.68 : 0.56);
  const parts: THREE.BufferGeometry[] = [];
  addTrunk(parts, trunkHeight, radius, detail, umbrella ? 0.82 : 1);

  if (detail === 'high') {
    const branchCount = umbrella ? 4 : 3;
    for (let i = 0; i < branchCount; i++) {
      const branchLength = radius * (umbrella ? 0.95 : 0.65);
      const branch = nonIndexed(new THREE.CylinderGeometry(radius * 0.035, radius * 0.065, branchLength, 5));
      branch.translate(0, branchLength / 2, 0);
      branch.rotateZ(Math.PI * (umbrella ? 0.39 : 0.28));
      branch.rotateY((i / branchCount) * Math.PI * 2);
      branch.translate(0, trunkHeight * 0.78, 0);
      paintVertexColor(branch, BARK_TONE);
      parts.push(branch);
    }
  }

  const blobCount = detail === 'high' ? (umbrella ? 7 : 6) : 3;
  const blobRadius = radius * (umbrella ? 0.44 : detail === 'high' ? 0.5 : 0.58);
  for (let i = 0; i < blobCount; i++) {
    const blob = new THREE.IcosahedronGeometry(blobRadius * (0.82 + 0.28 * jitter(i + 1)), 0);
    const angle = (i / blobCount) * Math.PI * 2 + jitter(i + 20) * 0.35;
    const ring = i === 0 ? 0 : radius * (umbrella ? 0.62 : 0.45) * (0.8 + jitter(i + 30) * 0.25);
    const y = umbrella
      ? trunkHeight + blobRadius * (0.28 + jitter(i + 40) * 0.22)
      : trunkHeight + blobRadius * (0.45 + jitter(i + 40) * 0.65);
    blob.scale(umbrella ? 1.28 : 1, umbrella ? 0.68 : 0.9 + jitter(i + 50) * 0.2, umbrella ? 1.1 : 1);
    blob.translate(Math.cos(angle) * ring, y, Math.sin(angle) * ring);
    paintVertexColor(blob, WHITE);
    parts.push(blob);
  }
  return mergeGeometries(parts, false);
}

function buildConiferTree(height: number, radius: number, detail: SolidDetail): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  addTrunk(parts, height * 0.82, radius, detail, 0.62);
  const layerCount = detail === 'high' ? 5 : 3;
  for (let i = 0; i < layerCount; i++) {
    const t = i / Math.max(1, layerCount - 1);
    const cone = nonIndexed(
      new THREE.ConeGeometry(radius * (1 - t * 0.58), height * (detail === 'high' ? 0.31 : 0.38), detail === 'high' ? 8 : 6),
    );
    cone.translate(0, height * (0.34 + t * 0.48), 0);
    paintVertexColor(cone, WHITE);
    parts.push(cone);
  }
  return mergeGeometries(parts, false);
}

function buildPalmTree(height: number, radius: number, detail: SolidDetail): THREE.BufferGeometry {
  const trunkHeight = height * 0.82;
  const parts: THREE.BufferGeometry[] = [];
  addTrunk(parts, trunkHeight, radius, detail, 0.55);
  const frondCount = detail === 'high' ? 10 : 6;
  for (let i = 0; i < frondCount; i++) {
    const angle = (i / frondCount) * Math.PI * 2;
    const length = radius * (1.2 + jitter(i + 70) * 0.35);
    const frond = buildBlade(length, radius * 0.16, -length * 0.14);
    frond.rotateZ(Math.PI * 0.48);
    frond.rotateY(angle);
    frond.translate(0, trunkHeight, 0);
    paintVertexColor(frond, WHITE);
    parts.push(frond);
  }
  return mergeGeometries(parts, false);
}

function buildStalkTree(height: number, radius: number, detail: SolidDetail, alien: boolean): THREE.BufferGeometry {
  const stalkHeight = height * (alien ? 0.76 : 0.72);
  const parts: THREE.BufferGeometry[] = [];
  addTrunk(parts, stalkHeight, radius, detail, alien ? 0.5 : 0.72);
  const capCount = detail === 'high' ? (alien ? 4 : 3) : 1;
  for (let i = 0; i < capCount; i++) {
    const cap = new THREE.IcosahedronGeometry(radius * (alien ? 0.62 : 0.9) * (0.9 + jitter(i + 80) * 0.2), 0);
    cap.scale(alien ? 0.65 : 1.15, alien ? 1.25 : 0.38, alien ? 0.65 : 1.15);
    const angle = (i / capCount) * Math.PI * 2;
    const spread = capCount > 1 ? radius * 0.36 : 0;
    cap.translate(Math.cos(angle) * spread, stalkHeight + radius * (alien ? 0.55 : 0.12), Math.sin(angle) * spread);
    paintVertexColor(cap, WHITE);
    parts.push(cap);
  }
  return mergeGeometries(parts, false);
}

function buildTree(species: VegetationSpeciesDef, height: number, radius: number, detail: SolidDetail): THREE.BufferGeometry {
  switch (treeSilhouette(species)) {
    case 'conifer': return buildConiferTree(height, radius, detail);
    case 'umbrella': return buildBroadleafTree(height, radius, detail, true);
    case 'palm': return buildPalmTree(height, radius, detail);
    case 'fungal': return buildStalkTree(height, radius, detail, false);
    case 'alien': return buildStalkTree(height, radius, detail, true);
    case 'broadleaf': return buildBroadleafTree(height, radius, detail, false);
  }
}

function buildBush(height: number, radius: number, detail: SolidDetail): THREE.BufferGeometry {
  const blobCount = detail === 'high' ? 6 : 3;
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < blobCount; i++) {
    const blob = new THREE.IcosahedronGeometry(radius * (0.52 + 0.24 * jitter(i + 2)), 0);
    const angle = (i / blobCount) * Math.PI * 2;
    const spread = i === 0 ? 0 : radius * 0.52;
    blob.scale(1, 0.7 + jitter(i + 11) * 0.25, 1);
    blob.translate(Math.cos(angle) * spread, height * (0.38 + jitter(i + 17) * 0.2), Math.sin(angle) * spread);
    paintVertexColor(blob, WHITE);
    parts.push(blob);
  }
  return mergeGeometries(parts, false);
}

/** Crossed tapering blades — used for grass, seagrass and kelp alike; the species' own height/radius already gives each its right silhouette. */
function buildBladeCluster(
  height: number,
  radius: number,
  detail: SolidDetail,
  tallStalks = false,
): THREE.BufferGeometry {
  const bladeCount = tallStalks ? (detail === 'high' ? 4 : 2) : detail === 'high' ? 13 : 4;
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < bladeCount; i++) {
    const bladeHeight = height * (0.72 + jitter(i + 90) * 0.35);
    const angle = (i / bladeCount) * Math.PI * 2;
    const blade = buildBlade(
      bladeHeight,
      radius * (tallStalks ? 0.18 : 0.13),
      radius * (jitter(i + 100) - 0.5) * (tallStalks ? 0.45 : 0.8),
    );
    blade.rotateY(angle);
    const ring = radius * (tallStalks ? 0.3 : 0.25 + jitter(i + 110) * 1.05);
    blade.translate(Math.cos(angle) * ring, 0, Math.sin(angle) * ring);
    paintVertexColor(blade, WHITE);
    parts.push(blade);
  }
  return mergeGeometries(parts, false);
}

function buildFlower(height: number, radius: number, detail: SolidDetail): THREE.BufferGeometry {
  const stemHeight = height * 0.7;
  const stem = buildBlade(stemHeight, radius * 0.08);
  paintVertexColor(stem, STEM_TONE);

  const parts: THREE.BufferGeometry[] = [stem];
  const petals = detail === 'high' ? 6 : 3;
  for (let i = 0; i < petals; i++) {
    const petal = new THREE.IcosahedronGeometry(radius * 0.48, 0);
    petal.scale(1, 0.28, 0.6);
    const angle = (i / petals) * Math.PI * 2;
    petal.rotateY(angle);
    petal.translate(Math.cos(angle) * radius * 0.52, stemHeight, Math.sin(angle) * radius * 0.52);
    paintVertexColor(petal, WHITE);
    parts.push(petal);
  }
  return mergeGeometries(parts, false);
}

/** Low-poly boulder: a few overlapping, irregularly scaled flat-faced icosahedra. */
function buildRock(height: number, radius: number, detail: SolidDetail): THREE.BufferGeometry {
  const chunkCount = detail === 'high' ? 4 : 2;
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < chunkCount; i++) {
    const chunkRadius = radius * (0.55 + 0.45 * jitter(i + 200));
    const chunk = new THREE.IcosahedronGeometry(chunkRadius, 0);
    chunk.scale(0.8 + jitter(i + 210) * 0.5, 0.55 + jitter(i + 220) * 0.4, 0.8 + jitter(i + 230) * 0.5);
    chunk.rotateY(jitter(i + 260) * Math.PI * 2);
    const angle = (i / chunkCount) * Math.PI * 2 + jitter(i + 240) * 0.6;
    const spread = i === 0 ? 0 : radius * 0.4 * jitter(i + 250);
    chunk.translate(Math.cos(angle) * spread, height * (0.28 + jitter(i + 270) * 0.22), Math.sin(angle) * spread);
    paintVertexColor(chunk, WHITE);
    parts.push(chunk);
  }
  return mergeGeometries(parts, false);
}

/** Cluster of angular shards jutting from a common base — quartz/ice/salt/obsidian all share this shape, coloured per species. */
function buildCrystal(height: number, radius: number, detail: SolidDetail): THREE.BufferGeometry {
  const shardCount = detail === 'high' ? 5 : 3;
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < shardCount; i++) {
    const shardHeight = height * (0.5 + jitter(i + 300) * 0.6);
    const shardRadius = radius * (0.16 + jitter(i + 310) * 0.14);
    const shard = nonIndexed(new THREE.ConeGeometry(shardRadius, shardHeight, 6));
    shard.translate(0, shardHeight / 2, 0);
    shard.rotateZ((jitter(i + 320) - 0.5) * 0.5);
    shard.rotateX((jitter(i + 330) - 0.5) * 0.5);
    const angle = (i / shardCount) * Math.PI * 2 + jitter(i + 340) * 0.5;
    const spread = i === 0 ? 0 : radius * (0.3 + jitter(i + 350) * 0.3);
    shard.translate(Math.cos(angle) * spread, 0, Math.sin(angle) * spread);
    paintVertexColor(shard, WHITE);
    parts.push(shard);
  }
  return mergeGeometries(parts, false);
}

function buildSolid(species: VegetationSpeciesDef, detail: SolidDetail): THREE.BufferGeometry {
  const height = (species.heightRange[0] + species.heightRange[1]) / 2;
  const radius = (species.radiusRange[0] + species.radiusRange[1]) / 2;
  switch (species.type) {
    case 'tree':
      return buildTree(species, height, radius, detail);
    case 'bush':
      return buildBush(height, radius, detail);
    case 'flower':
      return buildFlower(height, radius, detail);
    case 'grass':
    case 'seagrass':
      return buildBladeCluster(height, radius, detail);
    case 'kelp':
      return buildBladeCluster(height, radius, detail, true);
    case 'rock':
      return buildRock(height, radius, detail);
    case 'crystal':
      return buildCrystal(height, radius, detail);
  }
}

const solidGeometryCache = new Map<string, THREE.BufferGeometry>();

/** Species' solid-tier geometry, built once and reused for every instance across every chunk. */
export function getSolidGeometry(species: VegetationSpeciesDef, detail: SolidDetail): THREE.BufferGeometry {
  const key = `${species.id}:${detail}`;
  let geometry = solidGeometryCache.get(key);
  if (!geometry) {
    geometry = buildSolid(species, detail);
    solidGeometryCache.set(key, geometry);
  }
  return geometry;
}

/**
 * GLSL for a per-pixel dithered cutout, shared by the solid and billboard
 * fade paths. `uFade` is a plain 0..1 visibility fraction, not a
 * fade-in/fade-out direction: at 1 (the default on every non-transitioning
 * material) `vegDither(...) > 1.0` is never true since `fract()` never
 * reaches 1, so the discard never fires and steady-state rendering is
 * unchanged. `VegetationLayer` drives a *clone's* `uFade` from 0 to 1 (fading
 * in) or 1 to 0 (fading out) during an LOD tier transition; the same
 * comparison handles both directions, so no separate shader variant is
 * needed for 'incoming' vs 'outgoing'.
 */
const DITHER_FADE_GLSL = `
uniform float uFade;
float vegDither(vec2 co) { return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453); }
`;

/** Clones `uFade` onto its own uniform so a transitioning chunk can fade independently of every other chunk sharing the base material. */
function clonedFade(uniforms: { uFade?: { value: number } }, start: number): { value: number } {
  const fade = { value: start };
  uniforms.uFade = fade;
  return fade;
}

/**
 * Shared cascade-shadow uniforms for every solid-tier vegetation material
 * (the base and every fade clone — see `buildSolidMaterial`'s comment on why
 * one shared object reference is correct here, not a bug). `uSunDirectionWorld`
 * rides alongside the standard cascade set since MeshStandardMaterial has no
 * equivalent of TerrainMaterial's own `uSunDirection`: it lights instances
 * through Three's real `starLight`, which has no world-space direction
 * uniform of its own to borrow for the shadow bias's `dot(N, L)`.
 */
const vegetationShadowUniforms: ShadowCascadeUniforms & { uSunDirectionWorld: THREE.IUniform<THREE.Vector3> } = {
  ...createShadowCascadeUniforms(),
  uSunDirectionWorld: { value: new THREE.Vector3(0, 1, 0) },
};

/** For `Game.render()` to keep in sync with `LayeredRenderer.getShadowCascades()`/`starLight` once per frame. */
export function getVegetationShadowUniforms(): typeof vegetationShadowUniforms {
  return vegetationShadowUniforms;
}

/**
 * Vertex-shader world position for the shadow lookup, computed by hand
 * rather than relying on Three's own conditional `#include <worldpos_vertex>`
 * (which only fires under USE_ENVMAP/USE_SHADOWMAP/etc., none of which this
 * material sets) and applying the instance transform itself, since
 * `#include <begin_vertex>` runs before `instanceMatrix` is applied.
 */
const SHADOW_WORLDPOS_VERTEX_GLSL = `
varying vec3 vShadowWorldPos;
`;

/**
 * Self-shadow lookup for a solid vegetation instance: darkens the *direct*
 * term only (see TerrainMaterial's own comment on why ambient stays
 * unshadowed), injected between `lights_fragment_begin` (which sums direct
 * lights into `reflectedLight.directDiffuse/directSpecular`) and the indirect
 * terms added after it. `N` is approximated as world-up rather than the
 * instance's real normal — the bias it shapes only exists to fight
 * self-acne on a continuous surface sampling its own shadow, which a small
 * isolated instance never does, so an approximate `dot(N, L)` is enough.
 */
let solidMaterial: THREE.MeshStandardMaterial | null = null;

function buildSolidMaterial(): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.85,
    metalness: 0,
    side: THREE.DoubleSide,
  });
  const fade = { value: 1 };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uFade = fade;
    Object.assign(shader.uniforms, vegetationShadowUniforms);
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', `${SHADOW_WORLDPOS_VERTEX_GLSL}\nvoid main() {`)
      .replace(
        '#include <project_vertex>',
        `#include <project_vertex>
  vShadowWorldPos = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        `${DITHER_FADE_GLSL}
${GLSL_SHADOW_CASCADE_UNIFORMS}
uniform vec3 uSunDirectionWorld;
varying vec3 vShadowWorldPos;
${GLSL_SHADOW_CASCADE}
void main() {
  if (vegDither(gl_FragCoord.xy) > uFade) discard;`,
      )
      .replace(
        '#include <lights_fragment_begin>',
        `#include <lights_fragment_begin>
  float vegShadow = terrainSelfShadow(vShadowWorldPos, vec3(0.0, 1.0, 0.0), normalize(uSunDirectionWorld));
  reflectedLight.directDiffuse *= vegShadow;
  reflectedLight.directSpecular *= vegShadow;`,
      );
  };
  material.userData.fade = fade;
  return material;
}

/**
 * One material shared by every species' solid tiers; only vertex/instance
 * colour differs. Double-sided because a blade (grass/flower/kelp) is a
 * single flat triangle with only one true face — every species shares this
 * one material, so that has to be handled here rather than per geometry.
 */
export function getSolidMaterial(): THREE.MeshStandardMaterial {
  if (!solidMaterial) solidMaterial = buildSolidMaterial();
  return solidMaterial;
}

/**
 * An independent clone of the shared solid material for one actively
 * transitioning mesh. Cloning `onBeforeCompile` alone would keep pointing at
 * the *shared* `uFade` object, so this reassigns a fresh one after cloning —
 * the injected shader source is identical either way (Three reuses the
 * compiled program across clones; only the uniform value differs per mesh).
 */
export function cloneSolidMaterialForFade(start: number): { material: THREE.MeshStandardMaterial; fade: { value: number } } {
  const material = getSolidMaterial().clone();
  const fade = { value: start };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uFade = fade;
    shader.fragmentShader = shader.fragmentShader.replace(
      'void main() {',
      `${DITHER_FADE_GLSL}\nvoid main() {\n  if (vegDither(gl_FragCoord.xy) > uFade) discard;`,
    );
  };
  return { material, fade };
}

/** Average metric height/width for a species, used to size its billboard quad (the solid tiers bake this into geometry instead). */
export function speciesFootprint(species: VegetationSpeciesDef): { height: number; width: number } {
  return {
    height: (species.heightRange[0] + species.heightRange[1]) / 2,
    width: species.radiusRange[0] + species.radiusRange[1],
  };
}

let billboardQuad: THREE.BufferGeometry | null = null;

/** Unit quad (base at y=0, top at y=1) shared by every species' billboard tier. */
export function getBillboardQuad(): THREE.BufferGeometry {
  if (!billboardQuad) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array([-0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0]), 3),
    );
    geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    billboardQuad = geometry;
  }
  return billboardQuad;
}

export type BillboardSilhouette = TreeSilhouette | 'bush' | 'flower' | 'kelp' | 'grass' | 'rock' | 'crystal';

export function billboardSilhouette(species: VegetationSpeciesDef): BillboardSilhouette {
  if (species.type === 'tree') return treeSilhouette(species);
  if (species.type === 'bush') return 'bush';
  if (species.type === 'flower') return 'flower';
  if (species.type === 'kelp') return 'kelp';
  if (species.type === 'rock') return 'rock';
  if (species.type === 'crystal') return 'crystal';
  return 'grass';
}

function buildBillboardTexture(style: BillboardSilhouette): THREE.Texture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);

  const ellipse = (x: number, y: number, rx: number, ry: number, foliage = true, angle = 0): void => {
    ctx.save();
    ctx.translate(x * size, y * size);
    ctx.rotate(angle);
    ctx.scale(rx * size, ry * size);
    ctx.beginPath();
    ctx.arc(0, 0, 1, 0, Math.PI * 2);
    ctx.fillStyle = foliage ? '#fff' : '#000';
    ctx.fill();
    ctx.restore();
  };

  const trunk = (bottomWidth: number, topWidth: number, topY: number, lean = 0): void => {
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.moveTo((0.5 - bottomWidth * 0.5) * size, size);
    ctx.lineTo((0.5 + bottomWidth * 0.5) * size, size);
    ctx.lineTo((0.5 + lean + topWidth * 0.5) * size, topY * size);
    ctx.lineTo((0.5 + lean - topWidth * 0.5) * size, topY * size);
    ctx.closePath();
    ctx.fill();
  };

  const triangle = (cx: number, top: number, halfWidth: number, bottom: number): void => {
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.moveTo(cx * size, top * size);
    ctx.lineTo((cx + halfWidth) * size, bottom * size);
    ctx.lineTo((cx - halfWidth) * size, bottom * size);
    ctx.closePath();
    ctx.fill();
  };

  switch (style) {
    case 'broadleaf':
      trunk(0.13, 0.075, 0.48);
      ellipse(0.31, 0.38, 0.24, 0.2);
      ellipse(0.54, 0.28, 0.29, 0.23);
      ellipse(0.75, 0.41, 0.22, 0.19);
      ellipse(0.5, 0.48, 0.34, 0.18);
      break;
    case 'conifer':
      trunk(0.1, 0.045, 0.18);
      triangle(0.5, 0.06, 0.2, 0.42);
      triangle(0.5, 0.19, 0.29, 0.61);
      triangle(0.5, 0.35, 0.36, 0.79);
      break;
    case 'umbrella':
      trunk(0.12, 0.055, 0.39, -0.025);
      ellipse(0.26, 0.34, 0.25, 0.12, true, -0.08);
      ellipse(0.53, 0.27, 0.31, 0.14);
      ellipse(0.77, 0.36, 0.22, 0.11, true, 0.08);
      ellipse(0.51, 0.4, 0.39, 0.12);
      break;
    case 'palm':
      trunk(0.105, 0.04, 0.25, 0.04);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = size * 0.07;
      ctx.lineCap = 'round';
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(size * 0.54, size * 0.25);
        ctx.quadraticCurveTo(
          size * (0.54 + Math.cos(angle) * 0.23),
          size * (0.2 + Math.sin(angle) * 0.09),
          size * (0.54 + Math.cos(angle) * 0.4),
          size * (0.3 + Math.sin(angle) * 0.16),
        );
        ctx.stroke();
      }
      break;
    case 'fungal':
      trunk(0.16, 0.08, 0.3);
      ellipse(0.5, 0.27, 0.38, 0.14);
      break;
    case 'alien':
      trunk(0.09, 0.04, 0.27, -0.035);
      ellipse(0.44, 0.22, 0.14, 0.23);
      ellipse(0.62, 0.31, 0.12, 0.2, true, 0.25);
      ellipse(0.29, 0.36, 0.1, 0.17, true, -0.28);
      break;
    case 'bush':
      ellipse(0.25, 0.67, 0.23, 0.23);
      ellipse(0.5, 0.58, 0.29, 0.29);
      ellipse(0.76, 0.69, 0.22, 0.21);
      ellipse(0.5, 0.78, 0.39, 0.18);
      break;
    case 'flower':
      trunk(0.035, 0.018, 0.3);
      ellipse(0.5, 0.27, 0.18, 0.1);
      break;
    case 'kelp': {
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.moveTo(size * 0.42, size);
      ctx.quadraticCurveTo(size * 0.66, size * 0.5, size * 0.5, 0);
      ctx.quadraticCurveTo(size * 0.56, size * 0.5, size * 0.58, size);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'grass': {
      const blades = 7;
      for (let i = 0; i < blades; i++) {
        const bx = size * (0.15 + (i / (blades - 1)) * 0.7);
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.moveTo(bx - size * 0.025, size);
        ctx.lineTo(bx + size * 0.025, size);
        ctx.lineTo(bx + (i - 3) * size * 0.018, size * (0.18 + 0.12 * (i % 3)));
        ctx.closePath();
        ctx.fill();
      }
      break;
    }
    case 'rock':
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.moveTo(size * 0.15, size * 0.95);
      ctx.lineTo(size * 0.08, size * 0.6);
      ctx.lineTo(size * 0.28, size * 0.35);
      ctx.lineTo(size * 0.55, size * 0.42);
      ctx.lineTo(size * 0.78, size * 0.3);
      ctx.lineTo(size * 0.92, size * 0.58);
      ctx.lineTo(size * 0.85, size * 0.95);
      ctx.closePath();
      ctx.fill();
      break;
    case 'crystal':
      triangle(0.3, 0.15, 0.12, 0.95);
      triangle(0.5, 0.02, 0.16, 0.95);
      triangle(0.7, 0.22, 0.11, 0.95);
      break;
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/**
 * View-space billboard: the corner offset is added in view space (where
 * 'right' and 'up' are always +X/+Y regardless of camera orientation), so
 * every instance faces the camera without any per-instance CPU work. Scale
 * comes from the instance matrix's own basis-vector lengths rather than a
 * separate uniform, since 'instanceMatrix' already carries the per-instance
 * width/height baked in by 'VegetationLayer'.
 *
 * 'instanceMatrix' and 'instanceColor' are NOT declared here even though the
 * shader uses both: Three.js's WebGLProgram already injects
 * 'attribute mat4 instanceMatrix' and 'attribute vec3 instanceColor' into
 * every shader — custom or not — whenever the object being drawn is an
 * InstancedMesh with instance colours set. Declaring them again is a
 * duplicate-attribute error that fails to compile with no useful message
 * beyond 'Vertex shader is not compiled'.
 */
const BILLBOARD_VERTEX = /* glsl */ `
uniform vec3 uSunColor;
uniform float uSunIntensity;
varying vec2 vUv;
varying vec3 vColor;
varying float vLight;
varying float vDistance;

void main() {
  vUv = uv;
  vColor = instanceColor;
  // MeshStandardMaterial's diffuse response is considerably dimmer than a
  // raw albedo multiply. Keep the cards in the same tonal family as solid
  // medium trees while still responding to daylight changes.
  // uSunIntensity is already local-horizon gated by Game. The small floor
  // matches the scene hemisphere light; it keeps silhouettes legible without
  // making billboard foliage glow on the night side.
  vLight = 0.05 + 0.55 * min(1.4, uSunIntensity) * dot(uSunColor, vec3(0.333));

  vec4 centerView = viewMatrix * modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
  vDistance = length(centerView.xyz);
  float cardWidth = length(instanceMatrix[0].xyz);
  float cardHeight = length(instanceMatrix[1].xyz);
  vec4 mvPosition = centerView + vec4(position.x * cardWidth, position.y * cardHeight, 0.0, 0.0);
  gl_Position = projectionMatrix * mvPosition;
}
`;

const BILLBOARD_FRAGMENT = /* glsl */ `
uniform sampler2D uBillboardMap;
uniform float uFade;
uniform float uFadeNear;
uniform float uFadeFar;
varying vec2 vUv;
varying vec3 vColor;
varying float vLight;
varying float vDistance;

// A plain 0..1 visibility fraction, not a fade direction: at the default 1.0
// this discard never fires ('fract()' never reaches 1.0), so steady-state
// rendering is unchanged. 'VegetationLayer' drives a clone's 'uFade' toward
// 0 or 1 during an LOD tier transition; the same test covers fading in and
// fading out.
float vegDither(vec2 co) {
  return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  vec4 texel = texture2D(uBillboardMap, vUv);
  if (texel.a < 0.32) discard;
  float distanceVisibility = 1.0 - smoothstep(uFadeNear, uFadeFar, vDistance);
  if (vegDither(gl_FragCoord.xy) > uFade * distanceVisibility) discard;
  // Billboards always face the camera, so a flat vertical gradient stands in
  // for shading a 'real' normal cannot meaningfully provide here.
  float groundShade = mix(0.45, 0.82, vUv.y);
  vec3 baseColor = mix(vec3(0.28, 0.18, 0.09), vColor, texel.r);
  float aerialShade = mix(1.0, 0.90, smoothstep(500.0, 2500.0, vDistance));
  vec3 litColor = baseColor * groundShade * max(0.25, vLight) * aerialShade;
  gl_FragColor = vec4(litColor, 1.0);
}
`;

const billboardMaterialCache = new Map<BillboardSilhouette, THREE.ShaderMaterial>();

/** Shared, per-silhouette billboard material; colour still varies per instance. */
export function getBillboardMaterial(species: VegetationSpeciesDef): THREE.ShaderMaterial {
  const style = billboardSilhouette(species);
  let material = billboardMaterialCache.get(style);
  if (!material) {
    material = new THREE.ShaderMaterial({
      uniforms: {
        uBillboardMap: { value: buildBillboardTexture(style) },
        uSunColor: { value: new THREE.Color(1, 1, 1) },
        uSunIntensity: { value: 1 },
        uFade: { value: 1 },
        uFadeNear: { value: 1e9 },
        uFadeFar: { value: 1e9 + 1 },
      },
      vertexShader: BILLBOARD_VERTEX,
      fragmentShader: BILLBOARD_FRAGMENT,
      side: THREE.DoubleSide,
    });
    billboardMaterialCache.set(style, material);
  }
  return material;
}

/**
 * An independent clone of a billboard material for one actively transitioning
 * mesh — deep-clones the uniforms dict (the alpha texture reference is kept,
 * only `uFade` needs to be independent) so its fade never affects the shared
 * per-silhouette material every non-transitioning chunk still uses.
 */
export function cloneBillboardMaterialForFade(
  species: VegetationSpeciesDef,
  start: number,
): { material: THREE.ShaderMaterial; fade: { value: number } } {
  const base = getBillboardMaterial(species);
  const material = base.clone();
  material.uniforms = THREE.UniformsUtils.clone(base.uniforms);
  material.uniforms.uBillboardMap.value = base.uniforms.uBillboardMap.value;
  const fade = clonedFade(material.uniforms, start);
  return { material, fade };
}

/** Every billboard material currently built, for per-frame lighting updates. */
export function liveBillboardMaterials(): IterableIterator<THREE.ShaderMaterial> {
  return billboardMaterialCache.values();
}
